/**
 * autoStreamKey.js
 *
 * Auto-regenera el stream key para YouTube (OAuth2) y Facebook (Graph API)
 * justo antes de activar una publication desde el switch de la lista.
 */

import ytOAuth from './ytOAuth';

const YT_API = 'https://www.googleapis.com/youtube/v3';
const FB_API = 'https://graph.facebook.com/v19.0';

// ─── helpers ──────────────────────────────────────────────────────────────────

function fbExtractKey(streamUrl) {
	if (!streamUrl) return '';
	const parts = streamUrl.split('/');
	return parts[parts.length - 1] || '';
}

// ─── YouTube ──────────────────────────────────────────────────────────────────

async function refreshYoutube(restreamer, channelid, egressId, svc) {
	const { account_key, title, description, privacy_status, scheduled_start, mode, primary, backup } = svc;
	if (!account_key) return false;

	let token;
	try {
		token = await ytOAuth.getValidToken(account_key);
	} catch (e) {
		console.warn('[autoStreamKey] YouTube token refresh failed:', e.message);
		return false;
	}

	const headers = { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' };
	const startTime = scheduled_start
		? new Date(scheduled_start).toISOString()
		: new Date(Date.now() + 5 * 60 * 1000).toISOString();

	try {
		// 1) LiveBroadcast
		const bResp = await fetch(`${YT_API}/liveBroadcasts?part=snippet,status,contentDetails`, {
			method: 'POST', headers,
			body: JSON.stringify({
				snippet: { title: title || 'Live Stream', description: description || '', scheduledStartTime: startTime },
				status: { privacyStatus: privacy_status || 'public', selfDeclaredMadeForKids: false },
				contentDetails: { enableAutoStart: true, enableAutoStop: true, enableDvr: true, recordFromStart: true },
			}),
		});
		const bData = await bResp.json();
		if (!bResp.ok) throw new Error('Broadcast: ' + (bData.error ? bData.error.message : `HTTP ${bResp.status}`));
		const broadcastId = bData.id;

		// 2) LiveStream
		const sResp = await fetch(`${YT_API}/liveStreams?part=snippet,cdn,status`, {
			method: 'POST', headers,
			body: JSON.stringify({
				snippet: { title: title || 'Live Stream' },
				cdn: { frameRate: 'variable', ingestionType: 'rtmp', resolution: 'variable' },
				contentDetails: { isReusable: false },
			}),
		});
		const sData = await sResp.json();
		if (!sResp.ok) throw new Error('Stream: ' + (sData.error ? sData.error.message : `HTTP ${sResp.status}`));
		const streamId = sData.id;
		const streamKey = sData.cdn?.ingestionInfo?.streamName || '';

		// 3) Bind
		await fetch(`${YT_API}/liveBroadcasts/bind?id=${broadcastId}&part=id,contentDetails&streamId=${streamId}`, {
			method: 'POST', headers,
		});

		// 4) Persistir nuevos settings en metadata
		const newSvc = { ...svc, stream_key: streamKey, oauth_broadcast_id: broadcastId, oauth_stream_id: streamId };

		// Obtener metadata completa, parchear settings, guardar
		const meta = await restreamer.GetEgressMetadata(channelid, egressId);
		meta.settings = newSvc;
		await restreamer.SetEgressMetadata(channelid, egressId, meta);

		// 5) Actualizar outputs del proceso ffmpeg
		const newOutputs = buildYoutubeOutputs(newSvc);
		if (newOutputs.length > 0) {
			await restreamer.UpdateEgressStreamKey(channelid, egressId, newOutputs);
		}

		console.info('[autoStreamKey] YouTube stream key refreshed:', streamKey, '| broadcast:', broadcastId);
		return true;
	} catch (e) {
		console.warn('[autoStreamKey] YouTube refresh error:', e.message);
		return false;
	}
}

function buildYoutubeOutputs(s) {
	const outputs = [];
	if (!s.stream_key) return outputs;
	const options = ['-f', 'flv'];
	if (s.primary !== false) {
		outputs.push({ address: 'rtmps://a.rtmp.youtube.com/live2/' + s.stream_key, options: [...options] });
	}
	if (s.backup) {
		outputs.push({ address: 'rtmps://b.rtmp.youtube.com/live2?backup=1/' + s.stream_key, options: [...options] });
	}
	return outputs;
}

// ─── Facebook ─────────────────────────────────────────────────────────────────

async function refreshFacebook(restreamer, channelid, egressId, svc) {
	const { page_access_token, account_type, page_id, title, description } = svc;
	if (!page_access_token) return false;

	try {
		const subject = account_type === 'page' && page_id ? encodeURIComponent(page_id) : 'me';
		const form = new URLSearchParams();
		if (title) form.append('title', title);
		if (description) form.append('description', description);
		form.append('status', 'LIVE_NOW');
		form.append('access_token', page_access_token);

		const resp = await fetch(`${FB_API}/${subject}/live_videos`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body: form.toString(),
		});
		const data = await resp.json();

		if (!resp.ok || data.error) {
			console.warn('[autoStreamKey] Facebook error:', data.error ? `[#${data.error.code}] ${data.error.message}` : `HTTP ${resp.status}`);
			return false;
		}

		const streamUrl = data.stream_url || data.secure_stream_url || '';
		if (!streamUrl) {
			console.warn('[autoStreamKey] Facebook did not return a stream_url');
			return false;
		}

		const key = fbExtractKey(streamUrl);
		const newSvc = { ...svc, stream_key_primary: key };

		// Persistir settings actualizados
		const meta = await restreamer.GetEgressMetadata(channelid, egressId);
		meta.settings = newSvc;
		await restreamer.SetEgressMetadata(channelid, egressId, meta);

		// Actualizar outputs del proceso ffmpeg
		const newOutputs = buildFacebookOutputs(newSvc);
		if (newOutputs.length > 0) {
			await restreamer.UpdateEgressStreamKey(channelid, egressId, newOutputs);
		}

		console.info('[autoStreamKey] Facebook stream key refreshed:', key, '| live_id:', data.id);
		return true;
	} catch (e) {
		console.warn('[autoStreamKey] Facebook refresh error:', e.message);
		return false;
	}
}

function buildFacebookOutputs(s) {
	const outputs = [];
	const options = ['-f', 'flv'];
	if (s.stream_key_primary && s.rtmp_primary !== false) {
		outputs.push({ address: 'rtmps://live-api-s.facebook.com:443/rtmp/' + s.stream_key_primary, options: [...options] });
	}
	if (s.stream_key_backup && s.rtmp_backup) {
		outputs.push({ address: 'rtmps://live-api-s.facebook.com:443/rtmp/' + s.stream_key_backup, options: [...options] });
	}
	return outputs;
}

// ─── Entry point ──────────────────────────────────────────────────────────────

/**
 * Intenta refrescar el stream key del egress antes de iniciarlo.
 * Solo actúa en YouTube con account_key OAuth2 o Facebook con page_access_token.
 *
 * @param {object} restreamer
 * @param {string} channelid
 * @param {string} egressId    - id completo (restreamer-ui:egress:youtube:xxx)
 * @param {string} service     - 'youtube' | 'facebook' | ...
 * @returns {Promise<boolean>} true si actualizó algo
 */
export async function refreshEgressStreamKey(restreamer, channelid, egressId, service) {
	if (service !== 'youtube' && service !== 'facebook') return false;

	let meta;
	try {
		meta = await restreamer.GetEgressMetadata(channelid, egressId);
	} catch (e) {
		console.warn('[autoStreamKey] Could not read egress metadata:', e.message);
		return false;
	}

	// Los settings del servicio están en meta.settings (guardados por Edit.js)
	const svc = (meta && meta.settings) ? meta.settings : {};

	if (service === 'youtube') {
		// Solo refresh automático si hay account_key (OAuth2 conectado)
		if (!svc.account_key) return false;
		return await refreshYoutube(restreamer, channelid, egressId, svc);
	}

	if (service === 'facebook') {
		// Solo refresh automático si hay access token
		if (!svc.page_access_token) return false;
		return await refreshFacebook(restreamer, channelid, egressId, svc);
	}

	return false;
}
